import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import BookingPage, { DemoBanner, Flow, SiteUnavailable, reportFunnel, useSite, type Site } from './BookingPage';
import { TEMPLATES, LandingOneOffFluffyPaw } from './LandingTemplates';
import { LandingOneOffShowShears } from './LandingOneOffShowShears';
import { ErrorBoundary } from './ErrorBoundary';
import ReviewPage, { parseReviewPath } from './ReviewPage';
import PayPage, { parsePayPath } from './PayPage';

/*
 * The tenant's public site. Two paths, chosen without a router:
 *   /book  → the booking flow on its own (a direct link, and what confirmation emails point at)
 *   /      → the landing page, with that same flow embedded below the fold
 *
 * ⚠️ THE OLD "REQUEST A BOOKING" FORM IS GONE, and its removal is the point of #89. It
 * POSTed to /api/leads — a PLATFORM-plane table with no tenant_id, readable only by
 * StartMeUpPro staff. A customer filling it in created no booking, was never seen by the
 * operator, and was told "We'll be in touch shortly" by nobody who would be. It was left
 * over from before the booking flow existed. The case it appeared to serve — "I want to ask
 * before committing" — is handled inside the flow by quote-mode services, which do reach
 * the operator.
 *
 * ⚠️ ONE CTA. "Book an appointment" scrolls to the booking section on this page rather than
 * navigating away. A second competing call to action is what made this page confusing.
 */
export default function App() {
  /*
   * ⚠️ BOTH ENTRY POINTS ARE WRAPPED, and they are wrapped SEPARATELY so the console line
   * names which one died. Until this existed the app had no boundary anywhere: any throw
   * during render unmounted the tree and left a customer looking at a white page with no
   * message and nothing reported. See ErrorBoundary.tsx.
   */
  /*
   * `/review/<token>` and `/review/<token>/no` — where a "how did we do?" email lands (0050).
   * Checked before /book because it is the only path here carrying a secret, and because a
   * customer who mistypes it should get this page's "that link isn't valid" rather than a
   * booking form.
   */
  const review = parseReviewPath(window.location.pathname);
  if (review) {
    return (
      <ErrorBoundary surface="/review">
        <ReviewPage token={review.token} optOut={review.optOut} />
      </ErrorBoundary>
    );
  }

  // `/pay/<token>` — the "Pay now" link in an invoice email (0077). Carries a secret, so it is
  // checked here with /review, ahead of every catch-all.
  const pay = parsePayPath(window.location.pathname);
  if (pay) {
    return (
      <ErrorBoundary surface="/pay">
        <PayPage token={pay.token} />
      </ErrorBoundary>
    );
  }

  /*
   * `/privacy` and `/terms` — the tenant's own legal pages (#253).
   *
   * ⚠️ CHECKED BEFORE THE CATCH-ALL BELOW, which is the whole trap. The fallthrough renders
   * either her landing page or the booking form for EVERY unmatched path, so a route added
   * downstream of it does not 404 — it silently serves the booking form at the URL we gave a
   * carrier as a privacy policy. That is a campaign rejection with no error anywhere to find.
   *
   * ⚠️ AND THEY MUST STAY REACHABLE ON A TENANT WITH NO LANDING PAGE. `LandingPage` falls
   * back to `<BookingPage />` when `site.landing` is null, so the pages cannot live inside
   * the landing tree — they would vanish for exactly the tenants most likely to need them.
   */
  const path = window.location.pathname.replace(/\/+$/, '');
  /*
   * ⚠️ /privacy AND /terms ARE NOT ROUTES HERE ANY MORE (2026-09-21). Caddy sends them to the
   * API, which renders them as HTML — see services/api/src/lib/legal-pages.ts. They were React
   * routes until two A2P submissions were rejected for having no policy and no terms: the
   * fetcher that checks them does not run JavaScript, so the documents did not exist as far as
   * it could tell. If a route for them reappears here, that regression comes back with it.
   */

  if (path === '/book') {
    return (
      <ErrorBoundary surface="/book">
        <BookingPage />
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary surface="landing">
      <LandingPage />
    </ErrorBoundary>
  );
}

function LandingPage() {
  const { site, failure } = useSite();

  if (failure) return <SiteUnavailable failure={failure} />;
  if (!site) {
    return (
      <div className="grid min-h-screen place-items-center px-6">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // No landing page configured, or the tenant turned it off: the domain is for booking, so
  // show booking. Not an error state — a deliberate one.
  if (!site.landing) return <BookingPage />;

  return <Landing site={site} />;
}

function Landing({ site }: { site: Site }) {
  // Her home page seen, as opposed to someone starting a booking (BookingPage's 'flow_start').
  useEffect(() => reportFunnel('view:landing'), []);
  const { accentColor, logoUrl, logoVersion } = site.branding;
  const landing = site.landing!;

  /*
   * ⚠️ TWO BACKGROUNDS ON ONE SCROLL, ON PURPOSE (0038). The landing page may now carry its
   * own background while the booking flow embedded below the fold keeps the booking one, so
   * this component paints two canvases rather than one.
   *
   * ⚠️ THE PAIR FALLS BACK TOGETHER, NEVER HALF AND HALF. When the tenant has made no
   * separate choice the landing colors are BOTH the booking ones — taking the booking
   * background but a separately-derived text color is how you get grey-on-grey. The server
   * sends `textColor` as null in exactly the case `backgroundColor` is null, so this reads as
   * one decision rather than two.
   *
   * The ACCENT is deliberately not split (see 0038): buttons, links and prices stay one
   * color across both canvases, which is what stops the seam reading as two different
   * businesses stacked on top of each other.
   */
  const bookingBg = site.branding.backgroundColor;
  const bookingText = site.branding.textColor;
  const backgroundColor = landing.backgroundColor ?? bookingBg;
  const textColor = landing.textColor ?? bookingText;
  const landingAccent = landing.accentColor ?? accentColor;
  const splitCanvas = backgroundColor !== bookingBg;

  /*
   * ⚠️ THE TEMPLATE SUBTREE IS HANDED A SITE WHOSE `branding` IS THE LANDING PALETTE, rather
   * than every template being taught about a second one. All three templates and the bespoke
   * one already read `site.branding`, and threading a parallel set of colors through each of
   * them is four places to forget — a template added later would silently paint booking
   * colors on the website.
   *
   * The booking `Flow` below is deliberately given the ORIGINAL `site`, so it keeps the
   * booking palette. That is the whole split: same component, two palettes, decided here.
   */
  const landingSite: Site = {
    ...site,
    branding: { ...site.branding, backgroundColor, textColor, accentColor: landingAccent },
  };
  /*
   * ⚠️ ONE-OFFS ARE KEYED ON THE TENANT, NOT ON `template`. The platform offers three
   * templates and that is the whole catalogue; a bespoke page is paid work and is
   * deliberately not selectable from the picker, so it must not occupy a template value that
   * a founder could hand to somebody else by accident (decision: Mike, 2026-08-13).
   *
   * Adding the next one is a line in this map and a component. If this map ever gets long,
   * that is the signal the "just build one-offs" bet has stopped paying.
   */
  const ONE_OFFS: Record<string, typeof TEMPLATES.hero> = {
    fluffypawspa: LandingOneOffFluffyPaw,
    /*
     * Her slug before it was shortened on 2026-08-13. Kept because this map is the ONLY thing
     * tying her page to her tenant — rename the slug without touching it and her site quietly
     * falls back to the stock `hero` template, with nothing failing and nothing logged. Safe
     * to delete once the rename has bedded in.
     */
    'fluffy-paw-spa': LandingOneOffFluffyPaw,
    'show-shears-grooming': LandingOneOffShowShears,
  };
  const oneOff = ONE_OFFS[site.business.slug];
  const Template = oneOff ?? TEMPLATES[landing.template] ?? TEMPLATES.hero;
  const onAccent = readableOn(landingAccent);

  /*
   * ⚠️ THE GROOMER TEMPLATE DRAWS ITS OWN MASTHEAD, so the shared one is suppressed for it —
   * otherwise the logo appears twice on the same screen: once shrunk into this bar and again
   * at full size beside the headline three centimetres below. The other three templates open
   * with words or a photo and still need this.
   */
  const ownsMasthead = Boolean(oneOff);

  /*
   * A service chosen from a landing-page card, handed down to the flow.
   *
   * ⚠️ THE NONCE IS WHAT MAKES A REPEAT PRESS COUNT. The flow is mounted once and never
   * remounts, so a bare id could not express "she pressed the same card again" — the prop
   * would be identical and the button would look dead. See the note on `Flow`'s `pick`.
   */
  /* Derived from her own palette so the card survives any colors she later picks — the
     same technique the one-off template uses for its bands. */
  const hairline = `color-mix(in srgb, ${textColor} 12%, transparent)`;
  const cardWash = `color-mix(in srgb, ${landingAccent} 4%, ${backgroundColor})`;

  const [pick, setPick] = useState<{ serviceId: string; nonce: number } | null>(null);
  const pickService = (serviceId: string) =>
    setPick((p) => ({ serviceId, nonce: (p?.nonce ?? 0) + 1 }));

  return (
    <div className="min-h-screen" style={{ backgroundColor, color: textColor }}>
      <DemoBanner site={site} />
      {!ownsMasthead && (
        <header className="mx-auto flex max-w-6xl items-center px-5 pt-8">
          {logoUrl ? (
            <img
              src={`${import.meta.env.VITE_API_URL ?? 'http://localhost:4000'}${logoUrl}?v=${logoVersion}`}
              alt={site.business.name}
              className="max-h-12 max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-lg font-bold">{site.business.name}</span>
          )}
        </header>
      )}

      <Template site={landingSite} landing={landing} onAccent={onAccent} onPickService={pickService} />

      {/*
        The booking flow, inline. `scroll-mt` keeps the heading clear of the viewport edge
        when the CTA scrolls here — without it the section lands flush against the top and
        reads as though the page jumped rather than moved.
      */}
      {/*
        ⚠️ THE CARD IS SCOPED TO ONE-OFF PAGES, and that is deliberate rather than lazy. The
        three stock templates are the whole catalogue and they are shared by every tenant —
        restyling their booking section because one bespoke page wanted a card would change
        pages nobody asked me to touch. `max-w-6xl` + `rounded-3xl` matches the closing
        "Ready when your dog is." block directly above it, which is the size Mike asked for.

        Making it universal later is deleting the condition.
      */}
      {/*
        ⚠️ THE BOOKING SECTION PAINTS ITS OWN BACKGROUND when the two differ, and does NOT
        when they match. Setting it unconditionally would be harmless to look at and wrong in
        one specific way: a one-off page draws a card here tinted from the background, and
        stacking an identical opaque layer under it flattens that tint.

        Painted at all because the alternative is worse. Without it the booking flow inherits
        the landing canvas and the tenant's booking colors simply do not appear on `/`, which
        makes the Booking page tab's background control look broken. With it, the seam is a
        deliberate band — which is the trade accepted when the canvas was split.
      */}
      <section
        id="book"
        className="scroll-mt-6 py-12 sm:py-16"
        style={splitCanvas ? { backgroundColor: bookingBg, color: bookingText } : undefined}
      >
        <div className={ownsMasthead ? 'mx-auto max-w-6xl px-5' : ''}>
          <div
            className={ownsMasthead ? 'rounded-3xl px-4 py-10 sm:px-8 sm:py-14' : ''}
            style={ownsMasthead ? { border: `1px solid ${hairline}`, backgroundColor: cardWash } : undefined}
          >
            <div className={`mb-6 px-5 ${ownsMasthead ? 'text-center' : 'mx-auto max-w-2xl'}`}>
              <h2 className="text-2xl font-bold tracking-tight">Book an appointment</h2>
            </div>
            {/*
              A second boundary INSIDE the landing page, deliberately. The outer one would
              replace the whole page — business name, photos, reviews and all — because the
              booking form threw. Scoping it here means a broken form is a broken form, and
              the rest of the tenant's site is still there with a phone number on it.
            */}
            <ErrorBoundary surface="landing:booking-flow">
              <Flow site={site} embedded pick={pick} />
            </ErrorBoundary>
          </div>
        </div>
      </section>

      {/* Our name appears once, small, at the bottom — and nowhere else (#48). */}
      {/*
        ⚠️ THE LEGAL LINKS ARE IN THE FOOTER, NOT ONLY IN THE CAMPAIGN FORM. A carrier reviewer
        checks that the policy is reachable from the site, not just that a URL resolves.
      */}
      <footer className="mx-auto max-w-6xl px-5 pb-10 text-xs opacity-40">
        <a href="/privacy" className="underline">
          Privacy
        </a>
        {' · '}
        <a href="/terms" className="underline">
          Terms
        </a>
        {' · '}
        Booking powered by StartMeUpPro
      </footer>
    </div>
  );
}

/**
 * Black or white, whichever reads on the accent. Same relative-luminance rule the booking
 * flow uses, duplicated here rather than imported because it is four lines and exporting it
 * would widen BookingPage's surface for one caller.
 */
function readableOn(hex: string): string {
  const n = hex.replace('#', '');
  const full = n.length === 3 ? n.split('').map((c) => c + c).join('') : n;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [number, number, number];
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) < 0.5 ? '#FFFFFF' : '#111111';
}
